/**
 * Opening stock on a product's page: each entry as written, as it stands now,
 * and every correction the owner made — before and after, why, who, when.
 *
 * The owner corrects an entry while none of its stock has moved: the entry is
 * kept, a correction records what it was and what it becomes, and the shelf
 * and the item's average cost follow. Once its stock has moved, the past is
 * not restated; Adjust stock moves stock from there on.
 */

import { useState, type ReactNode } from 'react';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';
import { Badge, Button, Card, CardHeader, Input, Modal, Textarea } from '../../components/ui';
import { newKey, usePharmacy, usePharmacyWrite, type ListOf } from './data';
import { ErrorLine, Table, Td } from './parts';

export interface OpeningEntry {
  id: string;
  source: string | null;
  branchName: string;
  batchNumber: string | null;
  expiryDate: string | null;
  enteredAt: string;
  enteredQuantity: number;
  enteredUnitCost: string | null;
  quantity: number;
  unitCost: string | null;
  movedSince: boolean;
  corrections: Array<{
    id: string;
    reference: string;
    quantityBefore: number;
    quantityAfter: number;
    unitCostBefore: string | null;
    unitCostAfter: string | null;
    reason: string;
    correctedBy: string | null;
    correctedAt: string;
  }>;
}

interface CorrectionResult {
  reference: string;
  quantityBefore: number;
  quantityAfter: number;
  unitCostBefore: string | null;
  unitCostAfter: string | null;
}

export function OpeningStockCard({ productId, unitName }: { productId: string; unitName: string }): ReactNode {
  const { business, can } = useAuth();
  const c = business?.currencyCode ?? 'NGN';
  const list = usePharmacy<ListOf<OpeningEntry>>('/opening-stock', { productId });
  const [correcting, setCorrecting] = useState<OpeningEntry | null>(null);
  const owner = business?.role === 'owner' && can('pharmacy:stock_approve');
  const entries = list.data?.data ?? [];
  if (!entries.length) return null;
  const at = (q: number, cost: string | null) => `${q} at ${cost === null ? 'no cost' : money(cost, c)}`;

  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Opening stock" subtitle="What was on the shelf when the pharmacy joined CenoX, and every correction the owner made to it" />
      </div>
      <Table head={['Location', 'Batch', 'Entered', 'Now', 'Corrections', '']} minWidth={820}>
        {entries.map((e) => (
          <tr key={e.id}>
            <Td>{e.branchName}</Td>
            <Td className="font-mono text-xs">{e.batchNumber ? `${e.batchNumber} · exp ${e.expiryDate}` : '—'}</Td>
            <Td className="text-xs">
              {at(e.enteredQuantity, e.enteredUnitCost)}
              <p className="text-slate-500">{new Date(e.enteredAt).toLocaleString()}{e.source ? ` · ${e.source}` : ''}</p>
            </Td>
            <Td className="tabular-nums">{at(e.quantity, e.unitCost)} {unitName}</Td>
            <Td className="text-xs">
              {e.corrections.length === 0
                ? '—'
                : e.corrections.map((k) => (
                    <p key={k.id}>
                      <span className="font-mono">{k.reference}</span>: {at(k.quantityBefore, k.unitCostBefore)} → {at(k.quantityAfter, k.unitCostAfter)} ·{' '}
                      {k.correctedBy ?? '—'}, {new Date(k.correctedAt).toLocaleString()} · {k.reason}
                    </p>
                  ))}
            </Td>
            <Td className="text-right">
              {e.movedSince ? (
                <Badge tone="neutral">Stock has moved: use Adjust stock</Badge>
              ) : owner ? (
                <Button size="sm" variant="secondary" onClick={() => setCorrecting(e)}>Correct</Button>
              ) : null}
            </Td>
          </tr>
        ))}
      </Table>
      {correcting && <CorrectOpeningModal entry={correcting} currency={c} unitName={unitName} onClose={() => setCorrecting(null)} />}
    </Card>
  );
}

function CorrectOpeningModal({
  entry,
  currency,
  unitName,
  onClose,
}: {
  entry: OpeningEntry;
  currency: string;
  unitName: string;
  onClose: () => void;
}): ReactNode {
  const [quantity, setQuantity] = useState(String(entry.quantity));
  const [unitCost, setUnitCost] = useState(entry.unitCost ?? '');
  const [reason, setReason] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [key] = useState(newKey);
  const save = usePharmacyWrite<Record<string, unknown>, CorrectionResult>('post', `/opening-stock/${entry.id}/corrections`);
  const qtyOk = /^\d+$/.test(quantity.trim());
  const zero = qtyOk && Number(quantity) === 0;
  // With nothing left on the shelf the cost may stay as it was; stock that remains needs its cost.
  const costOk = (zero && !unitCost.trim()) || (/^\d+(\.\d{1,2})?$/.test(unitCost.trim()) && Number(unitCost) > 0);
  const unchanged = qtyOk && costOk && Number(quantity) === entry.quantity && entry.unitCost !== null && Number(unitCost) === Number(entry.unitCost);
  const ready = qtyOk && costOk && !unchanged && reason.trim().length >= 5;
  const done = save.data;
  const at = (q: number | string, cost: string | null) => `${q} ${unitName} at ${cost === null ? 'no cost' : money(cost, currency)}`;

  return (
    <Modal open onClose={onClose} title="Correct opening stock">
      <div className="space-y-3">
        {!done && !reviewing && (
          <>
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              Stands at <strong>{at(entry.quantity, entry.unitCost)}</strong>
              {entry.batchNumber ? `, batch ${entry.batchNumber}` : ''} · {entry.branchName}. The entry is kept as written; this records what it should have been.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input label={`Correct quantity (${unitName})`} inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} error={quantity && !qtyOk ? 'A whole number, zero or more' : undefined} />
              <Input label="Cost of one unit" inputMode="decimal" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} error={unitCost && !costOk ? 'An amount above zero, e.g. 4800.00' : undefined} />
            </div>
            <Textarea label="Why" value={reason} onChange={(e) => setReason(e.target.value)} />
            {unchanged && <p className="text-sm font-medium text-amber-700">That is what the entry stands at already: nothing to correct.</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button disabled={!ready} onClick={() => setReviewing(true)}>Review</Button>
            </div>
          </>
        )}
        {!done && reviewing && (
          <>
            <dl aria-label="Correction summary" className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 p-3 text-center">
              <div>
                <dt className="text-xs text-slate-500">Before</dt>
                <dd className="font-semibold">{at(entry.quantity, entry.unitCost)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">After</dt>
                <dd className="font-semibold">{zero ? `0 ${unitName}` : at(quantity, unitCost)}</dd>
              </div>
            </dl>
            <p className="text-sm text-slate-600">
              {reason.trim()}. The original entry and this correction both stay in the history. No sale or payment is recorded.
            </p>
            <ErrorLine error={save.error} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { save.reset(); setReviewing(false); }}>Back</Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  save.mutate({
                    quantity: Number(quantity),
                    ...(unitCost.trim() ? { unitCost: unitCost.trim() } : {}),
                    reason: reason.trim(),
                    expectedQuantity: entry.quantity,
                    idempotencyKey: key,
                  })
                }
              >
                Confirm correction
              </Button>
            </div>
          </>
        )}
        {done && (
          <>
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {done.reference}: opening stock corrected from {at(done.quantityBefore, done.unitCostBefore)} to {at(done.quantityAfter, done.unitCostAfter)}.
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Close</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
